import { SITE } from './site';

const E = SITE.email;

export const privacyDoc = {
  title: 'Privacy Policy',
  description:
    'How DashLink Gaming collects, uses, shares and protects your personal data, and the rights you have under GDPR, UK GDPR, US state laws, India’s DPDP Act and the UAE PDPL.',
  intro: `This Privacy Policy explains what personal data ${SITE.name} (“we”, “us”) collects when you use ${SITE.domain} and the DashLink Gaming web app (the “Service”), why we collect it, who we share it with and the choices you have. We have written it to be read by people, not just lawyers. If anything is unclear, email us at ${E}.`,
  sections: [
    {
      id: 'who-we-are',
      heading: '1. Who we are',
      body: [
        `${SITE.operator} operates the Service and is the “data controller” (GDPR / UK GDPR), “business” (US state privacy laws), “data fiduciary” (India Digital Personal Data Protection Act, 2023) and “controller” (UAE Federal Decree-Law No. 45 of 2021 on Personal Data Protection) for the personal data described here.`,
        `Contact for all privacy matters, including our Grievance Officer for users in India: ${E}.`,
      ],
    },
    {
      id: 'data-we-collect',
      heading: '2. Information we collect',
      body: [
        { sub: 'Information you give us' },
        {
          list: [
            'Account details: username, email address and password. Passwords are stored only as a one-way bcrypt hash — we never see or store your actual password.',
            'Profile details you choose to add, such as an avatar image link and bio.',
            'Chat messages, emojis and text you send in game chats and room chats. Messages are visible to other users in that chat and are stored so the chat history can be shown.',
            'Social activity: friends/favourites you save, “Looking for Squad” status, squad invites you send or receive, and watch-party links you share in a room.',
            'Messages you send us by email.',
          ],
        },
        { sub: 'Information created when you use the Service' },
        {
          list: [
            'Gameplay data: rooms you create or join, moves, wins, losses, draws, score and rank on the leaderboard.',
            'Security records: email verification and password-reset tokens (which expire), and short-lived rate-limit records (your email address and a timestamp, automatically deleted after about 15 minutes) used to stop abuse of our email features.',
            'Technical data: IP address, browser type, device type, pages visited and timestamps, which are processed by our hosting provider and — only if you allow analytics cookies — our analytics provider.',
          ],
        },
        { sub: 'Voice dictation' },
        'The microphone button in chat uses your browser’s built-in speech recognition (the Web Speech API) to turn your speech into text. We do not record, receive or store any audio. Depending on your browser, your browser vendor (for example Google for Chrome, Microsoft for Edge) may process the audio to produce the transcript under its own privacy policy. Only the resulting text — and only if you press send — reaches our servers as a chat message.',
        { sub: 'Information we do not collect' },
        'We do not collect payment card details, government ID numbers, precise location, or any “special category” / sensitive personal data. Please do not post such information in chats.',
      ],
    },
    {
      id: 'how-we-use',
      heading: '3. How we use your information and our legal bases',
      body: [
        {
          list: [
            'To create and secure your account, keep you signed in and verify your email — necessary to perform our contract with you (Terms of Service).',
            'To run multiplayer games, chats, the leaderboard, squad matching, invites, friends and watch parties — contract performance.',
            'To send transactional emails such as verification and password-reset messages — contract performance. We do not send marketing email.',
            'To prevent fraud, spam, cheating and abuse, enforce our Community Guidelines and keep the Service secure — our legitimate interests and legal obligations.',
            'To understand how the Service is used and improve it (analytics) — only with your consent.',
            'To show advertising, including personalised advertising where permitted — only with your consent where the law requires it (for example in the EU, EEA and UK).',
            'To comply with law, respond to lawful requests and defend legal claims — legal obligation and legitimate interests.',
          ],
        },
        'Where we rely on consent you can withdraw it at any time through “Cookie settings” in the footer, without affecting processing that happened before.',
      ],
    },
    {
      id: 'cookies',
      heading: '4. Cookies and similar technologies',
      body: [
        'We use a strictly necessary sign-in cookie and, only if you agree, analytics and advertising cookies. Full details, including how to change your choice, are in our Cookie Policy at /cookies.',
      ],
    },
    {
      id: 'advertising',
      heading: '5. Advertising (Google AdSense)',
      body: [
        'We may display ads served by Google AdSense to keep the Service free. Third-party vendors, including Google, use cookies to serve ads based on a user’s prior visits to this website or other websites.',
        'Google’s use of advertising cookies enables it and its partners to serve ads to you based on your visit to this site and/or other sites on the Internet. You may opt out of personalised advertising by visiting Google Ads Settings at https://adssettings.google.com, or opt out of some third-party vendors’ use of cookies for personalised advertising at https://www.aboutads.info/choices or https://www.youronlinechoices.eu.',
        'Learn how Google uses information from sites that use its services at https://policies.google.com/technologies/partner-sites. In the EU, EEA and UK no advertising cookies are set until you accept them; if you decline, any ads shown are non-personalised.',
      ],
    },
    {
      id: 'service-providers',
      heading: '6. Service providers and third parties',
      body: [
        'We share personal data only with providers that help us run the Service, under contracts that limit their use of the data:',
        {
          list: [
            'Cloud hosting and database providers — store and serve the app and your account data.',
            'Resend — delivers verification and password-reset emails (receives your email address and the email content).',
            'PostHog — product analytics, only if you accept analytics cookies.',
            'Google (AdSense) — advertising, as described in section 5.',
            'RAWG.io and FreeToGame — we fetch public game information (titles, cover art, genres) from them. We do not send them your personal data.',
            'Twitch and YouTube — when a watch-party stream is embedded, the video is loaded directly from Twitch or YouTube, which may set their own cookies and collect data under their own privacy policies.',
          ],
        },
        'We may also disclose information if required by law, to protect the rights, safety or property of our users or others, or as part of a merger or sale of the Service (in which case we will notify you).',
        'We do not sell your personal information, and we do not “share” it for cross-context behavioural advertising except through advertising cookies you have accepted.',
      ],
    },
    {
      id: 'public-info',
      heading: '7. Information other users can see',
      body: [
        'Your username, avatar, score, rank and game record appear on leaderboards, in rooms and in squad lists. If you set your profile to Private, only you and players you have saved as friends can see your profile and stats; others see “Private player” on leaderboards, though your username still appears in chats, rooms and squad lists you join. Chat messages are visible to everyone in that chat. Your email address and password are never shown to other users.',
      ],
    },
    {
      id: 'transfers',
      heading: '8. International transfers',
      body: [
        'Our providers may process data in countries other than yours, including the United States. Where data from the EU, EEA, UK, India or UAE is transferred abroad, we rely on appropriate safeguards such as the European Commission’s Standard Contractual Clauses, the UK International Data Transfer Addendum, adequacy decisions, or other mechanisms permitted by the applicable law.',
      ],
    },
    {
      id: 'retention',
      heading: '9. How long we keep data',
      body: [
        {
          list: [
            'Account, profile, stats and friends: for as long as your account is active. When you use “Delete My Account” they are deleted immediately (backups are overwritten within 30 days), except where we must keep limited records by law.',
            'Chat messages: for as long as the chat exists or until your account is deleted.',
            'Verification and reset tokens: until used or expired (reset links expire after 1 hour).',
            'Rate-limit records: about 15 minutes. “Looking for Squad” entries: about 30 minutes of inactivity.',
            'Analytics data: according to our analytics provider’s retention settings, typically no longer than 12 months.',
          ],
        },
      ],
    },
    {
      id: 'security',
      heading: '10. Security',
      body: [
        'We use encrypted HTTPS connections, bcrypt password hashing, httpOnly sign-in cookies that scripts cannot read, server-side validation of game moves, and rate limiting on sensitive actions. No system is perfectly secure; if we become aware of a breach affecting your data we will notify you and the relevant authorities as required by law.',
      ],
    },
    {
      id: 'your-rights',
      heading: '11. Your rights',
      body: [
        'Wherever you live, you can ask us to: access a copy of your data; correct inaccurate data; delete your data and account; and stop optional processing such as analytics and personalised ads. You can update most profile details yourself in the app, make your profile private, download all your data with “Export My Data” and permanently delete your account with “Delete My Account” (both in your profile). To make any other request email us at ' +
          E +
          ' from the address linked to your account. We will respond within 30 days (or sooner where local law requires) and will not discriminate against you for exercising your rights.',
        { sub: 'European Union / EEA and United Kingdom' },
        'Under the GDPR and UK GDPR you also have the right to restrict or object to processing based on legitimate interests, to data portability, to withdraw consent at any time, and to lodge a complaint with your local data protection authority (in the UK, the Information Commissioner’s Office at https://ico.org.uk).',
        { sub: 'United States' },
        'Residents of California (CCPA/CPRA) and other US states with comprehensive privacy laws (such as Virginia, Colorado, Connecticut, Utah and Texas) have the right to know what personal information we collect and why, to access, correct and delete it, and to opt out of the sale or sharing of personal information and of targeted advertising. We do not sell personal information. You can opt out of targeted advertising by declining advertising cookies in Cookie settings; we also honour Global Privacy Control (GPC) browser signals as an opt-out. You may use an authorised agent, and you may appeal any decision we make about your request by replying to our response.',
        { sub: 'India' },
        'Under the Digital Personal Data Protection Act, 2023, you (the “Data Principal”) have the right to obtain a summary of your personal data and processing, to correction, completion, updating and erasure, to grievance redressal, and to nominate another person to exercise your rights in the event of death or incapacity. You may contact our Grievance Officer at ' +
          E +
          '. If you are not satisfied with our response you may complain to the Data Protection Board of India.',
        { sub: 'United Arab Emirates' },
        'Under the UAE Personal Data Protection Law you have the right to obtain information about the processing of your data, to request correction or erasure, to restrict or stop processing, to object to automated processing, and to request transfer of your data. You may lodge a complaint with the UAE Data Office.',
      ],
    },
    {
      id: 'children',
      heading: '12. Children',
      body: [
        'The Service is not directed to children under 13 and we do not knowingly collect personal data from children under 13 (or under 16 in EU countries that set that age for online consent). If you are under 18, or under the age of digital consent where you live, you may only use the Service with the permission and supervision of a parent or guardian — in India, verifiable parental consent is required for users under 18. If you believe a child has given us personal data, contact ' +
          E +
          ' and we will delete it.',
      ],
    },
    {
      id: 'changes',
      heading: '13. Changes to this policy',
      body: [
        'We may update this policy as the Service changes. We will change the “Last updated” date above and, for significant changes, notify signed-in users in the app or by email before the change takes effect.',
      ],
    },
    {
      id: 'contact',
      heading: '14. Contact us',
      body: [`Questions, requests or complaints: ${E}. We aim to reply within 2–3 business days.`],
    },
  ],
};
